import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FilterableSelect } from '@/components/ui/filterable-select'
import { Plus, Trash2, Loader2 } from 'lucide-react'
import { CounterpartEditor, type CounterpartPayload } from './CounterpartEditor'
import {
  useAccounts,
  useAllOpenItems,
  useBankAccounts,
  useCreateMatch,
} from './hooks/useReconApi'
import type { Transaction, CounterpartKind } from './types'
import { formatCurrency } from './format'

interface SplitTabProps {
  transaction: Transaction
  clientId: string
  bankAccountId: number
  bankAccountLabel: string
}

interface SplitLine {
  id: string
  kind: CounterpartKind
  payload: CounterpartPayload | null
}

let lineIdCounter = 0
const nextLineId = () => `split-${++lineIdCounter}`

const KIND_OPTIONS = [
  { value: 'gl_account_post', label: 'Direct posting' },
  { value: 'mirage_open_item', label: 'Open Item' },
  { value: 'bank_account_transfer', label: 'Transfer' },
]

export function SplitTab({ transaction, clientId, bankAccountId, bankAccountLabel }: SplitTabProps) {
  const { isLoading: accLoading } = useAccounts(clientId)
  const { isLoading: oiLoading } = useAllOpenItems(clientId)
  const createMatch = useCreateMatch()
  const [lines, setLines] = useState<SplitLine[]>([
    { id: nextLineId(), kind: 'gl_account_post', payload: null },
  ])

  const txnAmount = Math.abs(transaction.amount)
  const isOutgoing = transaction.amount < 0

  const linesTotal = useMemo(
    () => lines.reduce((s, l) => s + (parseFloat(l.payload?.amount || '0') || 0), 0),
    [lines]
  )
  const remainder = txnAmount - linesTotal

  const updateLinePayload = (id: string, payload: CounterpartPayload | null) => {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, payload } : l)))
  }

  const addLine = () =>
    setLines((prev) => [
      ...prev,
      { id: nextLineId(), kind: 'gl_account_post', payload: null },
    ])
  const removeLine = (id: string) => setLines((prev) => prev.filter((l) => l.id !== id))
  const setLineKind = (id: string, kind: CounterpartKind) =>
    setLines((prev) =>
      prev.map((l) => (l.id === id ? { id: nextLineId(), kind, payload: null } : l))
    )

  const handleConfirm = () => {
    const counterparts = lines
      .map((l) => (l.payload ? { ...l.payload, counterpart_kind: l.kind } : null))
      .filter((p): p is CounterpartPayload => p !== null && parseFloat(p.amount) > 0)

    createMatch.mutate({
      transaction_id: transaction.id,
      client_id: clientId,
      mirage_bank_account_id: bankAccountId,
      match_kind: 'split',
      counterparts,
    })
  }

  if (accLoading || oiLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-neutral-400">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading...
      </div>
    )
  }

  const defaultAmount = (txnAmount / lines.length).toFixed(2)

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {lines.map((line, idx) => (
          <div key={line.id} className="relative space-y-2 rounded-md border border-neutral-200 bg-neutral-50/40 p-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-neutral-500">
                  Line {idx + 1}
                </span>
                <div className="w-[160px]">
                  <FilterableSelect
                    value={line.kind}
                    onValueChange={(v) => setLineKind(line.id, v as CounterpartKind)}
                    options={KIND_OPTIONS}
                    placeholder="Kind..."
                    searchPlaceholder="Search kind..."
                    triggerClassName="h-8 text-xs"
                  />
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => removeLine(line.id)}
                disabled={lines.length <= 1}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>

            {line.kind === 'bank_account_transfer' ? (
              <TransferLineEditor
                key={line.id}
                clientId={clientId}
                bankAccountId={bankAccountId}
                isOutgoing={isOutgoing}
                docDate={transaction.date || undefined}
                defaultNote={transaction.description?.slice(0, 200)}
                defaultAmount={defaultAmount}
                onChange={(payload) => updateLinePayload(line.id, payload)}
              />
            ) : line.kind === 'mirage_open_item' ? (
              <OpenItemLineEditor
                key={line.id}
                lineId={line.id}
                transaction={transaction}
                clientId={clientId}
                bankAccountId={bankAccountId}
                bankAccountLabel={bankAccountLabel}
                isOutgoing={isOutgoing}
                defaultAmount={defaultAmount}
                onChange={(payload) => updateLinePayload(line.id, payload)}
              />
            ) : (
              <CounterpartEditor
                match={{
                  id: line.id,
                  transaction_id: transaction.id,
                  state: 'unmatched',
                  match_kind: 'split',
                  mirage_bank_account_id: bankAccountId,
                  counterparts: [
                    {
                      id: `temp-${line.id}`,
                      counterpart_kind: line.kind,
                      dt_account_id: isOutgoing ? 0 : bankAccountId,
                      ct_account_id: isOutgoing ? bankAccountId : 0,
                      dt_apx_id: 0,
                      ct_apx_id: 0,
                      amount: defaultAmount,
                      note: transaction.description?.slice(0, 200),
                    },
                  ],
                  created_at: new Date().toISOString(),
                }}
                transaction={transaction}
                clientId={clientId}
                bankAccountId={bankAccountId}
                bankAccountLabel={bankAccountLabel}
                hideSave
                onChange={(cps) => updateLinePayload(line.id, cps[0] ?? null)}
              />
            )}
          </div>
        ))}
      </div>

      <Button variant="outline" size="sm" onClick={addLine}>
        <Plus className="mr-1 h-3.5 w-3.5" /> Add line
      </Button>

      <div className="flex items-center gap-4 text-sm">
        <span>Total: <strong>{formatCurrency(linesTotal, transaction.currency)}</strong></span>
        <span>Transaction: <strong>{formatCurrency(txnAmount, transaction.currency)}</strong></span>
        <span className={Math.abs(remainder) < 0.01 ? 'text-green-600' : 'text-amber-600'}>
          Remainder: <strong>{formatCurrency(remainder, transaction.currency)}</strong>
        </span>
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="blue"
          disabled={
            Math.abs(remainder) > 0.01 ||
            lines.every((l) => !l.payload || (!l.payload.dt_account_id && !l.payload.ct_account_id)) ||
            createMatch.isLoading
          }
          onClick={handleConfirm}
        >
          {createMatch.isLoading ? 'Creating...' : 'Confirm split'}
        </Button>
        {Math.abs(remainder) > 0.01 && lines.some((l) => l.payload) && (
          <span className="text-xs text-amber-600">
            Split amounts must equal the transaction amount
          </span>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Open Item sub-editor — pick an open item, then show CounterpartEditor like Match
// ---------------------------------------------------------------------------

interface OpenItemLineEditorProps {
  lineId: string
  transaction: Transaction
  clientId: string
  bankAccountId: number
  bankAccountLabel: string
  isOutgoing: boolean
  defaultAmount: string
  onChange: (payload: CounterpartPayload | null) => void
}

function OpenItemLineEditor({
  lineId,
  transaction,
  clientId,
  bankAccountId,
  bankAccountLabel,
  isOutgoing,
  defaultAmount,
  onChange,
}: OpenItemLineEditorProps) {
  const { data: openItems = [], isLoading } = useAllOpenItems(clientId)
  const [selectedKey, setSelectedKey] = useState<string>('')

  const options = useMemo(
    () =>
      openItems.map((oi) => ({
        value: `${oi.account_id}:${oi.apx_id}`,
        label: oi.counterparty_name || oi.doc_number || `APX ${oi.apx_id}`,
        details: formatCurrency(parseFloat(oi.balance), transaction.currency),
        searchText: `${oi.account_code} ${oi.counterparty_name || ''} ${oi.doc_number || ''} ${oi.doc_date || ''}`,
        code: oi.account_code,
      })),
    [openItems, transaction.currency]
  )

  const selectedOi = useMemo(() => {
    if (!selectedKey) return null
    const [accId, apxId] = selectedKey.split(':').map(Number)
    return openItems.find((oi) => oi.account_id === accId && oi.apx_id === apxId) || null
  }, [openItems, selectedKey])

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-xs text-neutral-400">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading open items...
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="space-y-1 rounded-md border border-neutral-200 bg-white p-3">
        <label className="block text-xs font-medium text-neutral-500">Open item</label>
        <FilterableSelect
          value={selectedKey}
          onValueChange={(v) => setSelectedKey(v)}
          options={options}
          placeholder="Select open item..."
          searchPlaceholder="Search counterparty / doc / account..."
          triggerClassName="h-9 text-sm"
          renderOption={renderCodedOption}
          renderSelectedValue={renderCodedOption}
        />
      </div>

      {selectedOi && (
        <CounterpartEditor
          key={`${lineId}-${selectedKey}`}
          match={{
            id: `${lineId}-${selectedKey}`,
            transaction_id: transaction.id,
            state: 'unmatched',
            match_kind: 'invoice_payment',
            mirage_bank_account_id: bankAccountId,
            counterparts: [
              {
                id: `temp-${lineId}`,
                counterpart_kind: 'mirage_open_item',
                dt_account_id: isOutgoing ? selectedOi.account_id : bankAccountId,
                ct_account_id: isOutgoing ? bankAccountId : selectedOi.account_id,
                dt_apx_id: isOutgoing ? selectedOi.apx_id : 0,
                ct_apx_id: isOutgoing ? 0 : selectedOi.apx_id,
                amount: defaultAmount,
                note: transaction.description?.slice(0, 200),
                doc_nom: selectedOi.doc_number || undefined,
                doc_date: selectedOi.doc_date || undefined,
              },
            ],
            created_at: new Date().toISOString(),
          }}
          transaction={transaction}
          clientId={clientId}
          bankAccountId={bankAccountId}
          bankAccountLabel={bankAccountLabel}
          hideSave
          onChange={(cps) => onChange(cps[0] ?? null)}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Transfer sub-editor — mirrors TransferTab (destination bank + amount), no journal preview
// ---------------------------------------------------------------------------

interface TransferLineEditorProps {
  clientId: string
  bankAccountId: number
  isOutgoing: boolean
  docDate?: string
  defaultNote?: string
  defaultAmount: string
  onChange: (payload: CounterpartPayload | null) => void
}

function TransferLineEditor({
  clientId,
  bankAccountId,
  isOutgoing,
  docDate,
  defaultNote,
  defaultAmount,
  onChange,
}: TransferLineEditorProps) {
  const { data: bankAccounts = [], isLoading } = useBankAccounts(clientId)
  const [destId, setDestId] = useState<number | null>(null)
  const [amount, setAmount] = useState(defaultAmount)
  const [note, setNote] = useState(defaultNote || '')

  const destOptions = useMemo(
    () =>
      bankAccounts
        .filter((a) => a.id !== bankAccountId)
        .map((a) => ({
          value: a.id.toString(),
          label: a.name,
          searchText: `${a.code}${a.sub_code ? `.${a.sub_code}` : ''} ${a.name}`,
          code: `${a.code}${a.sub_code ? `.${a.sub_code}` : ''}`,
        })),
    [bankAccounts, bankAccountId]
  )

  useEffect(() => {
    if (!destId || !amount || parseFloat(amount) <= 0) {
      onChange(null)
      return
    }
    onChange({
      counterpart_kind: 'bank_account_transfer',
      dt_account_id: isOutgoing ? destId : bankAccountId,
      ct_account_id: isOutgoing ? bankAccountId : destId,
      amount: parseFloat(amount).toFixed(4),
      note: note.slice(0, 200) || undefined,
      doc_date: docDate,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destId, amount, note])

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-xs text-neutral-400">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading bank accounts...
      </div>
    )
  }

  return (
    <div className="space-y-2 rounded-md border border-neutral-200 bg-white p-3">
      <div className="space-y-1">
        <label className="block text-xs font-medium text-neutral-500">
          Destination bank / cash account
        </label>
        <FilterableSelect
          value={destId?.toString() ?? ''}
          onValueChange={(v) => setDestId(v ? Number(v) : null)}
          options={destOptions}
          placeholder="Select destination account..."
          searchPlaceholder="Search bank account..."
          triggerClassName="h-9 text-sm"
          renderOption={renderCodedOption}
          renderSelectedValue={renderCodedOption}
        />
      </div>

      <div className="flex items-end gap-2">
        <div className="flex-1 space-y-1">
          <label className="block text-xs font-medium text-neutral-500">Note</label>
          <Input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 200))}
            placeholder="Description / основание"
            className="h-9 text-sm"
          />
        </div>
        <div className="w-[140px] space-y-1">
          <label className="block text-xs font-medium text-neutral-500">Amount</label>
          <Input
            type="number"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="h-9 text-right text-sm"
          />
        </div>
      </div>
    </div>
  )
}

const renderCodedOption = (opt: { label: string; details?: string; code?: string }) => (
  <span className="flex items-center gap-2 min-w-0">
    {opt.code && (
      <span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded shrink-0 font-mono">
        {opt.code}
      </span>
    )}
    <span className="truncate">{opt.label}</span>
    {opt.details && (
      <span className="ml-auto text-xs bg-neutral-100 text-neutral-700 px-1.5 py-0.5 rounded shrink-0 tabular-nums">
        {opt.details}
      </span>
    )}
  </span>
)
